import React, { useState, } from 'react';
import { EnhancedTableHead, } from './EnhancedTableHead';
import { EnhancedTableToolbar, } from './EnhancedTableToolbar';
import {
  Grid,
  Paper,
  Table,
  TableContainer,
  TablePagination,
  TableBody,
  styled,
  Button,
} from '@mui/material';
import styles from '../Styles';
import { Interfaces, } from '../../../config';
import { useNavigate, } from 'react-router-dom';
import { useAuth, } from '../../../context';
import LoadingComponent from '../../Loading';
import { useSnackbar, } from 'notistack';
import { tagsServices, } from '../../../services';
import { helpers, } from '../../../utils';
import { NoDataComponent, } from '../../NoDataComponent';
import { TagsTableRow, } from './TagsTableRow';
import { AxiosResponse, } from 'axios';

export const emptyNewBusinessTag = {
  name: '',
  description: '',
  collection: '',
  matchingPatterns: [] as Array<string>,
  matchingPatternsStr: '',
  isActive: true,
  rules: [],
};

const TagsTable = () => {
  const { enqueueSnackbar, } = useSnackbar();
  const [tags, setTags,] = useState<Interfaces.BusinessTag[]>([]);
  const [filteredTags, setFilteredTags,] = useState<Interfaces.BusinessTag[]>([]);
  const [loadingTags, setLoadingTags,] = useState<boolean>(false);
  const [filters, setFilters,] = React.useState({
    checked: false,
    order: 'asc',
    orderBy: 'tagsName',
    page: 0,
    rowsPerPage: 5,
    deletePopUp: false,
  });
  const history = useNavigate();
  const { isAuthorized, } = useAuth();
  const [readAccess,] = React.useState(isAuthorized('ADAPTIVE_TAG_READ'));
  const [writeAccess,] = React.useState(isAuthorized('ADAPTIVE_TAG_WRITE'));

  const fetchTags = async () => {
    setLoadingTags(true);
    try {
      const tagsResponse: AxiosResponse<Interfaces.BusinessTag[]> = await tagsServices.fetchTags();
      const _tags = tagsResponse.data.map((tag) => {
        if (!tag.rules) {
          tag.rules = [];
        }
        return tag;
      });
      const sortedTags = _tags.sort((a: Interfaces.BusinessTag, b: Interfaces.BusinessTag) => {
        return a.name.localeCompare(b.name);
      });
      setTags(sortedTags);
      setFilteredTags(sortedTags);
    } catch (error) {
      enqueueSnackbar(helpers.getErrorMessage(error), { variant: 'error', });
    } finally {
      setLoadingTags(false);
    }
  };

  const addTagFromCsv = (file: any) => {
    tagsServices.addTagFromCsv(file)
      .then(() => {
        enqueueSnackbar('Tags added successfully', { variant: 'success', });
        fetchTags();
      })
      .catch((error) => {
        enqueueSnackbar(helpers.getErrorMessage(error), { variant: 'error', });
      });
  };

  const descendingComparator = (a: any, b: any, orderBy: any) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order: any, orderBy: any) => {
    return order === 'desc'
      ? (a: any, b: any) => descendingComparator(a, b, orderBy)
      : (a: any, b: any) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (dataSources: any, comparator: any) => {
    const stabilizedThis = dataSources.map((el: any, index: number) => [
      el,
      index,
    ]);
    stabilizedThis.sort((a: any, b: any) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el: any) => el[0]);
  };

  const handleSearch = (event: any) => {
    let filteredData = tags.filter((value: any) => value.name.toLowerCase().includes(event.target.value.toLowerCase())
    );
    setFilteredTags(filteredData);
  };

  const handleRequestSort = (event: any, property: any) => {
    const isAsc = filters.orderBy === property && filters.order === 'asc';
    setFilters({
      ...filters,
      order: isAsc ? 'desc' : 'asc',
      orderBy: property,
    });
  };

  const handleChangePage = (event: any, newPage: number) => {
    setFilters({
      ...filters,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (event: any) => {
    setFilters({
      ...filters,
      rowsPerPage: parseInt(event.target.value, 10),
    });
  };

  const importFromCSV = (event: any) => {
    addTagFromCsv(event.target.files[0]);
  };

  const { order, orderBy, page, rowsPerPage, } = filters;
  const Input = styled('input')({
    display: 'none',
  });
  const classes = styles();

  React.useEffect(() => {
    fetchTags().finally();
  }, []);

  if (loadingTags && tags.length === 0) {
    return <LoadingComponent message={'Loading tags'}/>;
  }

  if (!loadingTags && tags.length === 0) {
    const navigationData = {
      component: 'tags',
      header: 'No Tags',
      text1: 'You have not configured a tag yet',
      text2: 'Create a tag to get started',
      buttonText: 'Add Business Tag',
      route: '/tag/add',
    };

    const tagsData = {
      buttonText2: 'Import from CSV',
    };
    return <NoDataComponent data={navigationData} tags={tagsData}/>;
  }

  return (
    <div className={classes.rootTableTags}>
      <Grid
        className={classes.dividerTags}
        container
        direction="row"
        alignItems="center"
      >
        { writeAccess && readAccess && (
          <Grid item xs={8} style = {{ marginBottom:'20px', }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                history('/tag/add');
              }}
              style = {{ marginRight:'10px', }}
            >
                add business tags
            </Button>
            <Input
              id="contained-button-file"
              type="file"
              accept=".csv"
              onChange={(event: any) => {
                importFromCSV(event);
              }}
              hidden
            />
            <label htmlFor="contained-button-file">
              <Button
                className={classes.buttonsMargin}
                variant="contained"
                color="primary"
                component="span"
              >
                Upload from csv
              </Button>
            </label>
          </Grid>
        ) }
      </Grid>
      <Paper className={classes.paperTags}>
        <EnhancedTableToolbar
          numSelected={0}
          handleSearch={handleSearch}
          checkedTags={[]}
        />
        <TableContainer>
          <Table
            className={classes.tableTags}
            aria-labelledby="tableTitle"
            size={'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {stableSort(
                filteredTags,
                getComparator(order, orderBy)
              )
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((tag: any, index: number) => {
                  return (
                    <TagsTableRow
                      key={index}
                      tag={tag}
                      index={index}
                      fetchTags={fetchTags}
                    />
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25,]}
          component="div"
          count={filteredTags.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
};

export { TagsTable, };
